import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../../../services/tokenService';
import { getDashboardRouteForRole } from '../../../utils/roleNavigation';

const API = '/api';

export default function useWorkflowPersistence({
  user, resumeId,
  souscriptionCreee, setSouscriptionCreee,
  etape, setEtape,
  formData, setFormData,
  // Document state setters
  _setDocumentsGeneres, setDocumentPdfUrl: setDocumentPdfUrl, initDocsForSouscription,
  // Signature state
  documentsLus, setDocumentsLus,
  documentScrolled, setDocumentScrolled,
  codeSMSEnvoye, setCodeSMSEnvoye,
  signatureValidee, setSignatureValidee,
  telephoneSignature, setTelephoneSignature,
  // FTPE state
  ftpeAdhesionId,
  setFtpeAdhesionId, setFtpeAccepted, setFtpeOtpEnvoye, setFtpeStatus, setFtpeTelephone,
}) {
  const navigate = useNavigate();
  const [loadingResume, setLoadingResume] = useState(!!resumeId);
  const [isResuming, setIsResuming] = useState(false);
  const [savingForLater, setSavingForLater] = useState(false);

  // Save progress to backend
  const saveProgress = useCallback(async () => {
    if (!souscriptionCreee?.id) return;
    try {
      const token = getToken();
      await axios.put(
        `${API}/souscriptions/${souscriptionCreee.id}/save-progress`,
        {
          etape,
          documents_lus: documentsLus,
          document_scrolled: documentScrolled,
          signature_sms_envoyee: codeSMSEnvoye,
          signature_validee: signatureValidee,
          telephone_signature: telephoneSignature,
          form_data: formData,
          // V36.96 — Persiste le lien FTPE pour éviter la fuite cross-entity au resume
          ftpe_adhesion_id: ftpeAdhesionId || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch { /* save best-effort, ignored */ }
  }, [souscriptionCreee?.id, etape, documentsLus, documentScrolled, codeSMSEnvoye, signatureValidee, telephoneSignature, formData, ftpeAdhesionId]);

  // Load souscription for resume
  const loadSouscriptionForResume = useCallback(async (souscriptionId) => {
    setLoadingResume(true);
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/souscriptions/${souscriptionId}/resume`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;

      if (!data.can_resume) {
        toast.error('Cette souscription ne peut pas etre reprise (deja payee)');
        navigate('/calculateur-batico');
        return;
      }

      setSouscriptionCreee(data.souscription);
      setFormData(data.form_data);
      setIsResuming(true);

      const workflowData = data.workflow_data || {};
      setDocumentsLus(workflowData.documents_lus || {});
      setDocumentScrolled(workflowData.document_scrolled || false);
      setCodeSMSEnvoye(workflowData.signature_sms_envoyee || false);
      setSignatureValidee(workflowData.signature_validee || false);
      setTelephoneSignature(workflowData.telephone_signature || data.form_data.telephone || '');

      // Restore FTPE
      if (data.ftpe_adhesion_id) {
        setFtpeAdhesionId(data.ftpe_adhesion_id);
        setFtpeAccepted(true);
        if (data.ftpe_telephone) setFtpeTelephone(data.ftpe_telephone);
        if (data.ftpe_status === 'signature_validated' || data.ftpe_status === 'completed' || data.ftpe_status === 'payment_received') {
          setFtpeStatus('signed');
        } else if (data.ftpe_status === 'sms_sent') {
          setFtpeOtpEnvoye(true);
        }
      }

      // Restore documents (non-bloquant : useDocuments gère la génération à l'étape 4)
      if (data.souscription.document_rcds_pack1 || data.souscription.document_rcds_path || data.etape_reprise >= 3) {
        initDocsForSouscription(souscriptionId);
      }

      setEtape(data.etape_reprise);
      toast.success(`Souscription reprise a l'etape ${data.etape_reprise}`);
    } catch {
      toast.error('Erreur lors du chargement de la souscription');
      navigate('/calculateur-batico');
    } finally {
      setLoadingResume(false);
    }
  }, [navigate, setSouscriptionCreee, setFormData, setDocumentsLus, setDocumentScrolled,
      setCodeSMSEnvoye, setSignatureValidee, setTelephoneSignature,
      setFtpeAdhesionId, setFtpeAccepted, setFtpeTelephone, setFtpeStatus, setFtpeOtpEnvoye,
      initDocsForSouscription, setEtape]);

  // Auto-save when step changes
  useEffect(() => {
    if (souscriptionCreee?.id && etape > 2) {
      saveProgress();
    }
  }, [etape, souscriptionCreee?.id, saveProgress]);

  // Save before unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (souscriptionCreee?.id && !signatureValidee && etape >= 3) {
        const token = getToken();
        const data = JSON.stringify({
          etape,
          documents_lus: documentsLus,
          document_scrolled: documentScrolled,
          signature_sms_envoyee: codeSMSEnvoye,
          signature_validee: signatureValidee,
          telephone_signature: telephoneSignature,
          form_data: formData
        });
        navigator.sendBeacon(
          `${API}/souscriptions/${souscriptionCreee.id}/save-progress-beacon?token=${token}`,
          new Blob([data], { type: 'application/json' })
        );
        e.preventDefault();
        e.returnValue = 'Votre souscription sera sauvegardee automatiquement. Etes-vous sur de vouloir quitter ?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [souscriptionCreee?.id, signatureValidee, etape, documentsLus, documentScrolled, codeSMSEnvoye, telephoneSignature, formData]);

  // Manual save and quit
  const handleSaveForLater = async () => {
    if (!souscriptionCreee?.id) {
      toast.error('Aucune souscription a sauvegarder');
      return;
    }
    setSavingForLater(true);
    try {
      await saveProgress();
      toast.success('Souscription sauvegardee ! Vous pouvez la reprendre depuis "Mes Documents".');
      setTimeout(() => {
        // V36.85 — Redirection role-aware (un super_admin reste dans son espace)
        navigate(getDashboardRouteForRole(user?.role));
      }, 1500);
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingForLater(false);
    }
  };

  return {
    loadingResume, isResuming, savingForLater,
    saveProgress, loadSouscriptionForResume, handleSaveForLater,
  };
}
